import React, { useState, useEffect } from 'react';
import BackButton from '@/components/BackButton';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { formatHoursHM } from '@/utils/formatHours';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import EmployeeAvatar from '@/components/EmployeeAvatar';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import SignaturePad from '@/components/SignaturePad';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLanguage } from '@/contexts/LanguageContext';
import { Clock, CalendarCheck, CalendarX, ClockClockwise, Play, Stop, SpinnerGap, Globe, SignOut, CaretDown, MapPin, User, CheckCircle, XCircle, Calculator, FileText, Info, Package } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Sar } from '@/components/SarIcon';
import { getDeviceId } from '@/utils/deviceId';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getDeptLabel } from '@/constants/departments';

import { Badge } from '@/components/ui/badge';
import LucentLoader from '@/components/LucentLoader';
import ThemeToggle from '@/components/ThemeToggle';
import ManagerReturnToWorkCard from '@/components/manager/ManagerReturnToWorkCard';
import ManagerTeamAttendanceCard from '@/components/manager/ManagerTeamAttendanceCard';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

// Percentage doctors (طبيب نسبة) & agency contracted workers do NOT
// accrue paid leave — their leaves are informational only. Hide balance
// widgets/allowance UI for them. Percentage doctors are auto-flagged
// `is_contracted_worker=True` at create/update time, but we double-check
// `doctor_type==='percentage'` defensively so legacy records without the
// flag still render correctly.
const hasNoPaidLeave = (u) =>
  !!(u && (u.is_contracted_worker || (u.is_doctor && u.doctor_type === 'percentage')));

export default function EmployeeDashboard() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { language, toggleLanguage, isRTL, direction } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  // Cooldown (seconds) after a successful clock-in during which the
  // clock-out button stays disabled. Prevents accidental double-taps
  // from immediately clocking the employee out.
  const [clockOutCooldown, setClockOutCooldown] = useState(0);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [eosResignation, setEosResignation] = useState(null);
  const [eosTermination, setEosTermination] = useState(null);
  const [myProfile, setMyProfile] = useState(null);
  const [deptLeaves, setDeptLeaves] = useState([]);
  const [processingLeave, setProcessingLeave] = useState(null);
  const [adherence, setAdherence] = useState(null);
  const [leavePayoutDays, setLeavePayoutDays] = useState('');
  const [accrualBreakdown, setAccrualBreakdown] = useState(null);
  // FIFO preview for the calculator — fetched from /api/accruals/preview so
  // the displayed allowance EXACTLY matches what /clearance/cash-only would
  // pay out (historic-cost FIFO over the oldest salary slices first).
  const [fifoPreview, setFifoPreview] = useState(null);
  const [fifoLoading, setFifoLoading] = useState(false);
  // Employee's active custody — show a compact list of assets held.
  const [myCustody, setMyCustody] = useState([]);
  const [pendingCustody, setPendingCustody] = useState([]);
  const [signCustodyTarget, setSignCustodyTarget] = useState(null);
  const [signCustodyEmpty, setSignCustodyEmpty] = useState(true);
  const [signCustodyName, setSignCustodyName] = useState('');
  const [signingCustody, setSigningCustody] = useState(false);
  const signCustodyPadRef = React.useRef(null);

  useEffect(() => {
    fetchData();
    // Fetch my custody separately — side rail widget, don't block
    // dashboard load if the custody route is unreachable on legacy pods.
    axios.get(`${API}/custody/my`, { withCredentials: true })
      .then((r) => setMyCustody(Array.isArray(r.data) ? r.data.filter((x) => x.status === 'active') : []))
      .catch(() => setMyCustody([]));
    axios.get(`${API}/custody/my-pending`, { withCredentials: true })
      .then((r) => setPendingCustody(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPendingCustody([]));
  }, []);

  const refetchCustody = async () => {
    try {
      const [a, p] = await Promise.all([
        axios.get(`${API}/custody/my`, { withCredentials: true }),
        axios.get(`${API}/custody/my-pending`, { withCredentials: true }),
      ]);
      setMyCustody((a.data || []).filter((x) => x.status === 'active'));
      setPendingCustody(p.data || []);
    } catch { /* silent */ }
  };

  const submitCustodySignature = async () => {
    if (!signCustodyTarget) return;
    if (signCustodyEmpty) {
      toast.error(language === 'ar' ? 'يرجى رسم توقيعك في المربع المخصص' : 'Please draw your signature');
      return;
    }
    const sigImage = signCustodyPadRef.current?.toDataURL();
    setSigningCustody(true);
    try {
      await axios.post(
        `${API}/custody/${signCustodyTarget.id}/employee-sign`,
        { signature_image: sigImage, signature_name: signCustodyName.trim() || user?.name, agreed: true },
        { withCredentials: true },
      );
      toast.success(language === 'ar' ? 'تم توقيع استلام العهدة بنجاح' : 'Custody signed successfully');
      setSignCustodyTarget(null);
      setSignCustodyName('');
      await refetchCustody();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل التوقيع' : 'Signing failed'));
    } finally {
      setSigningCustody(false);
    }
  };

  // Countdown ticker for the post-clock-in cooldown. When
  // `clockOutCooldown` is > 0 we tick it down every second until it
  // reaches zero, at which point the clock-out button becomes enabled.
  useEffect(() => {
    if (clockOutCooldown <= 0) return;
    const t = setTimeout(() => setClockOutCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [clockOutCooldown]);

  // Debounced FIFO preview so the calculator value matches what
  // /clearance/cash-only would actually pay out. Runs only for legacy hires
  // that have FIFO slices — simple-path employees compute locally.
  useEffect(() => {
    const days = parseFloat(leavePayoutDays);
    if (!days || days <= 0) {
      setFifoPreview(null);
      setFifoLoading(false);
      return;
    }
    if (!accrualBreakdown?.is_legacy_hire) {
      setFifoPreview(null);
      return;
    }
    setFifoLoading(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/accruals/preview`, {
          withCredentials: true,
          params: { days },
        });
        setFifoPreview(data);
      } catch (e) {
        setFifoPreview(null);
      } finally {
        setFifoLoading(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [leavePayoutDays, accrualBreakdown]);

  const fetchData = async () => {
    try {
      const [statsRes, attendanceRes, adhRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/attendance/today`, { withCredentials: true }),
        axios.get(`${API}/attendance/my-schedule-adherence`, { withCredentials: true }).catch(() => null)
      ]);
      setStats(statsRes.data);
      setTodayAttendance(attendanceRes.data);
      if (adhRes) setAdherence(adhRes.data);
      
      if (user?.id) {
        const [leaveRes, eosResignRes, eosTermRes, profileRes, accrualRes] = await Promise.all([
          axios.get(`${API}/employees/${user.id}/leave-balance`, { withCredentials: true }).catch(() => null),
          axios.get(`${API}/employees/${user.id}/eos?termination_type=resignation`, { withCredentials: true }).catch(() => null),
          axios.get(`${API}/employees/${user.id}/eos?termination_type=termination`, { withCredentials: true }).catch(() => null),
          axios.get(`${API}/employees/${user.id}`, { withCredentials: true }).catch(() => null),
          axios.get(`${API}/accruals/balance`, { withCredentials: true }).catch(() => null),
        ]);
        if (leaveRes) setLeaveBalance(leaveRes.data);
        if (eosResignRes) setEosResignation(eosResignRes.data);
        if (eosTermRes) setEosTermination(eosTermRes.data);
        if (profileRes) setMyProfile(profileRes.data);
        if (accrualRes) setAccrualBreakdown(accrualRes.data);

        // Manager view (org-chart): fetch leaves of direct subordinates
        // for any user with subordinates OR the legacy dept-manager flag.
        const isManagerForUI = profileRes?.data?.is_manager
          || profileRes?.data?.is_department_manager;
        if (isManagerForUI) {
          const deptRes = await axios.get(`${API}/leaves/department-pending`, { withCredentials: true }).catch(() => null);
          if (deptRes) setDeptLeaves(deptRes.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error(t('geo_not_supported')));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          // Browser-reported 68%-confidence radius in metres. Backend
          // rejects values > 100m (cell-tower / faked-GPS fallback).
          accuracy: pos.coords.accuracy ?? null,
          // Extra anti-spoofing metadata. Real device GPS exposes these
          // when moving; mock-GPS apps leave them null.
          heading: pos.coords.heading ?? null,
          speed: pos.coords.speed ?? null,
          altitude: pos.coords.altitude ?? null,
        }),
        (err) => reject(new Error(t('geo_denied'))),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  const handleClockIn = async () => {
    setClockingIn(true);
    try {
      const location = await getLocation();
      const payload = { ...location, device_id: getDeviceId() };
      const { data } = await axios.post(`${API}/attendance/clock-in`, payload, { withCredentials: true });
      if (data.late_minutes && data.late_minutes > 0) {
        toast.warning(
          language === 'ar'
            ? `تم التسجيل — تأخرت ${data.late_minutes} دقيقة (جزاء ${(data.late_penalty || 0).toFixed(2)} ر.س)`
            : `Clocked in — late by ${data.late_minutes} min (penalty ${(data.late_penalty || 0).toFixed(2)} SAR)`,
          { duration: 6000 }
        );
      } else {
        toast.success(t('clock_in_success'));
      }
      // Guard against accidental double-tap: disable clock-out for 5s.
      setClockOutCooldown(5);
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || t('clock_in_failed');
      toast.error(msg);
    } finally {
      setClockingIn(false);
    }
  };

  const handleClockOut = async () => {
    setClockingOut(true);
    try {
      const location = await getLocation();
      const payload = { ...location, device_id: getDeviceId() };
      await axios.post(`${API}/attendance/clock-out`, payload, { withCredentials: true });
      toast.success(t('clock_out_success'));
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || t('clock_out_failed');
      toast.error(msg);
    } finally {
      setClockingOut(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleManagerAction = async (leaveId, action) => {
    setProcessingLeave(leaveId);
    try {
      await axios.put(`${API}/leaves/${leaveId}/manager-${action}`, {}, { withCredentials: true });
      toast.success(action === 'approve' ? t('manager_approved') : t('rejected'));
      setDeptLeaves(prev => prev.filter(l => l.id !== leaveId));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setProcessingLeave(null);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    // Pin to Asia/Riyadh — backend stores UTC, business logic is Saudi.
    // 24-hour format (HH:mm) per company standard.
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Asia/Riyadh',
    });
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Format the payroll-cycle boundary as "26/01 → 25/02" for the
  // work-days card subtitle. Falls back to a generic label when the
  // backend hasn't returned the boundaries yet.
  const cycleLabel = (() => {
    if (!stats?.cycle_start || !stats?.cycle_end) return null;
    const fmt = (iso) => {
      const [, m, d] = iso.split('-');
      return `${d}/${m}`;
    };
    return `${fmt(stats.cycle_start)} → ${fmt(stats.cycle_end)}`;
  })();

  const statCards = [
    {
      icon: CalendarCheck,
      label: 'work_days_this_month',
      value: stats?.work_days_this_month || 0,
      subtitle: cycleLabel,
      color: 'bg-[#655dc6]',
    },
    { icon: ClockClockwise, label: 'total_hours_worked', value: `${stats?.total_hours || 0}h`, color: 'bg-[#10B981]' },
    { icon: Clock, label: 'pending_leaves', value: stats?.pending_leaves || 0, color: 'bg-[#F59E0B]' },
    { icon: CalendarX, label: 'approved_leaves', value: stats?.approved_leaves || 0, color: 'bg-[#7B6FD6]' },
  ];

  // Manual attendance state — only `manualSubmitting` is needed since
  // the click now submits the current time directly. Kept above the early
  // return so React rules-of-hooks order stays stable.
  const [manualSubmitting, setManualSubmitting] = useState(false);

  if (loading) {
    return <LucentLoader />;
  }

  const isClockedIn = todayAttendance?.clock_in && !todayAttendance?.clock_out;
  const isClockedOut = todayAttendance?.clock_out;
  // Multi-shift days: allow a second clock-in after the first clock-out
  // (e.g. employee finished shift 1 at 15:00, now starts shift 2 at 19:00).
  const canClockInAgain = Boolean(todayAttendance?.can_clock_in_again);
  const totalShifts = todayAttendance?.total_shifts || 1;
  const sessionsCompleted = todayAttendance?.sessions_completed || 0;
  // Doctors have no fixed schedule — hide clock-in/out entirely.
  const isDoctor = Boolean(user?.is_doctor);
  const showClockIn = !isDoctor && ((!isClockedIn && !isClockedOut) || (isClockedOut && canClockInAgain));
  const showCompleted = !isDoctor && isClockedOut && !canClockInAgain;
  const showClockOut = !isDoctor && isClockedIn;

  // Manual attendance mode — for employees explicitly flagged as
  // `manual_attendance_allowed` by admin. UX is now identical to a
  // regular clock-in click: the button submits immediately with the
  // current KSA local time. No dialog, no time picker — the GPS check
  // is the only thing skipped.
  const isManualMode = Boolean(user?.manual_attendance_allowed);

  const submitManualPunch = async (kind) => {
    setManualSubmitting(true);
    try {
      // Server uses its own clock — we deliberately don't send any time
      // from the device so the recorded punch time can't be influenced
      // by the employee's phone / timezone.
      const { data } = await axios.post(
        `${API}/attendance/manual-punch`,
        { kind },
        { withCredentials: true }
      );
      toast.success(data.message || (language === 'ar' ? 'تم بنجاح' : 'Done'));
      // Same 5s guard as regular clock-in — only applies to clock-in punches.
      if (kind === 'in') setClockOutCooldown(5);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'ar' ? 'فشل التسجيل' : 'Failed'));
    } finally {
      setManualSubmitting(false);
    }
  };

  const handleClockInClick = () => (isManualMode ? submitManualPunch('in') : handleClockIn());
  const handleClockOutClick = () => (isManualMode ? submitManualPunch('out') : handleClockOut());

  return (
    <div className="min-h-screen bg-[#F2F0FA]" data-testid="employee-dashboard" dir={direction}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] dark:border-slate-700 py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
            
            <div className="flex items-center gap-1 sm:gap-4">
              <BackButton className="text-[#655dc6]" />
              <ThemeToggle />
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="text-[#655dc6] hover:bg-[#E8E6F5] px-2 sm:px-3"
                aria-label={language === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
              >
                <Globe size={20} className="sm:me-2" />
                <span className="hidden sm:inline">{language === 'ar' ? 'EN' : 'عربي'}</span>
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-1 sm:gap-3 px-1 sm:px-3 hover:bg-[#E8E6F5]">
                    <EmployeeAvatar
                      userId={user?.id}
                      name={language === 'en' && user?.name_en ? user.name_en : user?.name}
                      photoPath={user?.photo_path}
                      photoUpdatedAt={user?.photo_updated_at}
                      size={36}
                    />
                    <CaretDown size={16} className="hidden sm:inline text-[#655dc6]/60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/employee/profile')} className="text-[#655dc6]" data-testid="profile-menu-item">
                    <User size={18} className="me-2" />
                    {t('my_profile')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/employee/requests')} className="text-[#655dc6]" data-testid="requests-menu-item">
                    <FileText size={18} className="me-2" />
                    {language === 'ar' ? 'طلباتي' : 'My Requests'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <SignOut size={18} className="me-2" />
                    {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Welcome Section */}
        <div className="flex items-center gap-4">
          <EmployeeAvatar
            userId={user?.id}
            name={language === 'en' && user?.name_en ? user.name_en : user?.name}
            photoPath={user?.photo_path}
            photoUpdatedAt={user?.photo_updated_at}
            size={72}
            fontClass="text-2xl"
            className="ring-2 ring-[#C4BFF0] shadow-sm"
          />
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-semibold text-[#655dc6] break-words">
              {t('welcome_back')}, {language === 'en' && user?.name_en ? user.name_en : user?.name}!
            </h1>
            <p className="text-[#655dc6]/70 mt-1 text-sm sm:text-base">{language === 'en' ? (user?.position_en || user?.position) : user?.position} - {getDeptLabel(user?.department, language)}</p>
          </div>
        </div>

        {/* Clock In/Out Card */}
        <Card className="border border-[#C4BFF0] dark:border-[#3d3785]/60 bg-gradient-to-br from-[#655dc6] via-[#5B52B5] to-[#544cb0] dark:from-[#2d2870] dark:via-[#252060] dark:to-[#1e1a52]" data-testid="attendance-card">
          <CardContent className="p-5 sm:p-6">
            <div className="text-white">
              {/* Date header */}
              <p className="text-white/70 text-sm mb-4">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Asia/Riyadh' })}</p>

              {isDoctor ? (
                <div className="px-4 py-3 bg-white/10 rounded-lg text-white text-sm text-center" data-testid="doctor-no-attendance">
                  {language === 'ar' ? 'طبيب — لا يلزمك تسجيل حضور/انصراف' : 'Doctor — no clock-in/out required'}
                </div>
              ) : showCompleted ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-white/70 text-xs uppercase tracking-wide mb-1">{t('clock_in')}</p>
                    <p className="text-2xl font-semibold">{formatTime(todayAttendance?.clock_in)}</p>
                  </div>
                  <div>
                    <p className="text-white/70 text-xs uppercase tracking-wide mb-1">{t('clock_out')}</p>
                    <p className="text-2xl font-semibold">{formatTime(todayAttendance?.clock_out)}</p>
                  </div>
                  <div className="col-span-2 mt-2 px-4 py-2 bg-white/10 rounded-lg text-white text-sm text-center">
                    {language === 'ar' ? 'انتهى دوامك لهذا اليوم' : 'Work completed for today'}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:gap-6">
                  {/* Clock In column */}
                  <div className="flex flex-col gap-3">
                    <div>
                      <p className="text-white/70 text-xs uppercase tracking-wide mb-1">{t('clock_in')}</p>
                      <p className="text-2xl sm:text-3xl font-semibold">{formatTime(todayAttendance?.clock_in)}</p>
                    </div>
                    <Button
                      onClick={handleClockInClick}
                      disabled={clockingIn || !showClockIn}
                      className="w-full bg-white text-[#655dc6] hover:bg-white/90 disabled:bg-white/20 disabled:text-white/50 disabled:cursor-not-allowed dark:bg-emerald-500 dark:text-white dark:hover:bg-emerald-600 dark:disabled:bg-emerald-500/25 dark:disabled:text-white/60"
                      data-testid="clock-in-btn"
                    >
                      {clockingIn ? (
                        <SpinnerGap size={20} className="animate-spin me-2" />
                      ) : (
                        <Play size={20} weight="fill" className="me-2" />
                      )}
                      {canClockInAgain
                        ? `${t('clock_in')} — ${language === 'ar' ? 'الوردية' : 'Shift'} ${sessionsCompleted + 1}/${totalShifts}`
                        : t('clock_in')}
                    </Button>
                  </div>

                  {/* Clock Out column */}
                  <div className="flex flex-col gap-3">
                    <div>
                      <p className="text-white/70 text-xs uppercase tracking-wide mb-1">{t('clock_out')}</p>
                      <p className="text-2xl sm:text-3xl font-semibold">{formatTime(todayAttendance?.clock_out)}</p>
                    </div>
                    <Button
                      onClick={handleClockOutClick}
                      disabled={clockingOut || !showClockOut || clockOutCooldown > 0}
                      className="w-full bg-red-500 hover:bg-red-600 text-white disabled:bg-red-500/25 disabled:text-white/60 disabled:cursor-not-allowed"
                      data-testid="clock-out-btn"
                    >
                      {clockingOut ? (
                        <SpinnerGap size={20} className="animate-spin me-2" />
                      ) : (
                        <Stop size={20} weight="fill" className="me-2" />
                      )}
                      {clockOutCooldown > 0
                        ? `${t('clock_out')} (${clockOutCooldown})`
                        : t('clock_out')}
                      {totalShifts > 1 && clockOutCooldown === 0 && (
                        <span className="ms-2 text-xs opacity-80">{sessionsCompleted + 1}/{totalShifts}</span>
                      )}
                    </Button>
                  </div>

                  {/* Work hours summary — spans full width. Overtime is
                      DELIBERATELY hidden here; the employee only sees OT
                      figures on the published payslip. */}
                  {(todayAttendance?.work_hours_raw || todayAttendance?.work_hours || 0) > 0 && (
                    <div className="col-span-2 mt-1 flex items-center gap-2 text-sm text-white/80">
                      <span className="text-white/70">{t('work_hours')}:</span>
                      <span className="font-semibold text-white">
                        {formatHoursHM(todayAttendance.work_hours_raw != null ? todayAttendance.work_hours_raw : todayAttendance.work_hours)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Custody awaiting signature — amber alert, keep right under
            the clock-in card so the employee sees it the moment they
            land on their dashboard. */}
        {pendingCustody.length > 0 && (
          <Card className="border-amber-300 bg-amber-50/40 dark:bg-amber-900/10" data-testid="pending-custody-card">
            <CardHeader>
              <CardTitle className="text-lg text-amber-800 dark:text-amber-200 flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-amber-500 text-white">!</span>
                {language === 'ar' ? 'عهد بانتظار توقيعك' : 'Custody awaiting your signature'}
                <span className="text-xs font-normal text-amber-700 dark:text-amber-300 ms-2">({pendingCustody.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {pendingCustody.map((r) => (
                  <div key={r.id} className="rounded-lg border border-amber-300 dark:border-amber-700/60 bg-white dark:bg-slate-900 p-3" data-testid={`pending-custody-${r.id}`}>
                    <div className="font-semibold text-[#262263] dark:text-amber-100">{r.asset?.name || '—'}</div>
                    {r.asset?.serial_number && (
                      <div className="text-[11px] font-mono text-gray-500 dark:text-slate-400 mt-0.5">SN: {r.asset.serial_number}</div>
                    )}
                    <div className="text-[11px] text-gray-500 dark:text-slate-400 mt-1">
                      {language === 'ar' ? 'طُلب في' : 'Requested on'}: <span className="font-mono">{(r.handover_at || '').slice(0, 10)}</span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => { setSignCustodyTarget(r); setSignCustodyName(user?.name || ''); setSignCustodyEmpty(true); setTimeout(() => signCustodyPadRef.current?.clear(), 50); }}
                      className="mt-3 w-full h-8 bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid={`sign-custody-btn-${r.id}`}
                    >
                      {language === 'ar' ? 'توقيع الآن' : 'Sign now'}
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current active custody — compact list of what the employee
            is personally holding. Rendered near the top so it's always
            above the fold alongside the leave-balance / salary cards. */}
        {myCustody.length > 0 && (
          <Card className="border-[#C4BFF0] dark:border-slate-700 bg-white dark:bg-slate-900" data-testid="my-custody-card">
            <CardHeader>
              <CardTitle className="text-lg text-[#655dc6] dark:text-violet-300 flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-200">
                  <Package size={20} weight="duotone" />
                </span>
                {language === 'ar' ? 'عهدي الحالية' : 'My current custody'}
                <span className="text-xs font-normal text-[#655dc6]/60 dark:text-slate-400 ms-2">
                  {language === 'ar' ? `(${myCustody.length} صنف)` : `(${myCustody.length} items)`}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {myCustody.map((r) => (
                  <div key={r.id} className="rounded-lg border border-[#C4BFF0] dark:border-slate-700 bg-[#F8F7FC] dark:bg-slate-800/60 p-3" data-testid={`my-custody-${r.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[#262263] dark:text-slate-100 truncate">{r.asset?.name || '—'}</div>
                        {r.asset?.serial_number && (
                          <div className="text-[11px] font-mono text-gray-500 dark:text-slate-400 mt-0.5">SN: {r.asset.serial_number}</div>
                        )}
                        {r.asset?.brand && (
                          <div className="text-[11px] text-gray-500 dark:text-slate-400">{r.asset.brand} {r.asset.model && `· ${r.asset.model}`}</div>
                        )}
                      </div>
                      <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-200">
                        {language === 'ar' ? 'نشطة' : 'Active'}
                      </span>
                    </div>
                    <div className="text-[11px] text-gray-500 dark:text-slate-400 mt-2 flex items-center justify-between">
                      <span>
                        {language === 'ar' ? 'استلمت في' : 'Received'}: <span className="font-mono">{(r.handover_at || '').slice(0, 10)}</span>
                      </span>
                      <a
                        href={`${API}/custody/${r.id}/receipt.pdf?kind=handover`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#655dc6] dark:text-violet-300 hover:underline"
                        data-testid={`my-custody-receipt-${r.id}`}
                      >
                        {language === 'ar' ? 'عرض السند' : 'View receipt'}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/60 rounded px-3 py-2">
                {language === 'ar'
                  ? '⚠ يجب استرجاع جميع العهد قبل التمكن من توقيع المخالصة النهائية.'
                  : '⚠ All custody items must be returned before you can sign the final clearance.'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Schedule Adherence */}
        {adherence && (
          <Card className="border border-[#C4BFF0]" data-testid="schedule-adherence-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <h3 className="text-lg font-semibold text-[#655dc6] flex items-center gap-2">
                  <Clock size={22} weight="duotone" />
                  {language === 'ar' ? 'الالتزام بالجدول — الدورة الحالية' : 'Schedule Adherence — Current Cycle'}
                </h3>
                <div className="flex items-center gap-2 text-xs text-[#655dc6]/70 bg-[#E8E6F5] px-3 py-1.5 rounded-full" dir="ltr">
                  <span>{adherence.schedule.start_time} - {adherence.schedule.end_time}</span>
                  <span className="text-[#655dc6]/40">|</span>
                  <span>{adherence.schedule.daily_hours}h/{language === 'ar' ? 'يوم' : 'day'}</span>
                  <span className="text-[#655dc6]/40">|</span>
                  <span>{language === 'ar' ? 'سماح' : 'Grace'} {adherence.schedule.grace_minutes}m</span>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-green-50 rounded-lg text-center" data-testid="adherence-present">
                  <p className="text-xs text-green-600">{language === 'ar' ? 'أيام الحضور' : 'Days Present'}</p>
                  <p className="text-2xl font-bold text-green-700">{adherence.days_present}</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg text-center" data-testid="adherence-late-days">
                  <p className="text-xs text-orange-600">{language === 'ar' ? 'أيام التأخير' : 'Late Days'}</p>
                  <p className="text-2xl font-bold text-orange-700">{adherence.days_late}</p>
                </div>
                <div className="p-3 bg-yellow-50 rounded-lg text-center" data-testid="adherence-late-minutes">
                  <p className="text-xs text-yellow-700">{language === 'ar' ? 'إجمالي دقائق التأخير' : 'Total Late Minutes'}</p>
                  <p className="text-2xl font-bold text-yellow-800">{adherence.total_late_minutes}</p>
                </div>
                <div className={`p-3 rounded-lg text-center ${adherence.total_late_penalty > 0 ? 'bg-red-50' : 'bg-[#E8E6F5]'}`} data-testid="adherence-penalty">
                  <p className={`text-xs ${adherence.total_late_penalty > 0 ? 'text-red-600' : 'text-[#655dc6]/70'}`}>{language === 'ar' ? 'إجمالي الجزاء' : 'Total Penalty'}</p>
                  <p className={`text-2xl font-bold ${adherence.total_late_penalty > 0 ? 'text-red-700' : 'text-[#655dc6]'}`}>
                    {adherence.total_late_penalty.toLocaleString()} <span className="text-sm">{language === 'ar' ? 'ر.س' : 'SAR'}</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {statCards.map((stat, index) => (
            <Card
              key={index}
              className="border border-[#C4BFF0] hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
              data-testid={`stat-card-${stat.label}`}
            >
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-[#655dc6]/70 mb-1 line-clamp-1">{t(stat.label)}</p>
                    <p className="text-2xl sm:text-3xl font-semibold text-[#655dc6]">{stat.value}</p>
                    {stat.subtitle && (
                      <p className="text-[10px] text-[#655dc6]/60 mt-1 font-mono" dir="ltr" data-testid={`stat-subtitle-${stat.label}`}>
                        {stat.subtitle}
                      </p>
                    )}
                  </div>
                  <div className={`p-2 sm:p-3 rounded-lg ${stat.color} shrink-0`}>
                    <stat.icon size={22} weight="duotone" className="text-white sm:w-7 sm:h-7" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Manager view — pending leaves of direct subordinates (org chart) */}
        {(myProfile?.is_manager || myProfile?.is_department_manager) && (
          <ManagerReturnToWorkCard />
        )}
        {(myProfile?.is_manager || myProfile?.is_department_manager || myProfile?.is_general_manager) && (
          <ManagerTeamAttendanceCard language={language} />
        )}
        {(myProfile?.is_manager || myProfile?.is_department_manager) && (
          <Card className="border border-[#C4BFF0]" data-testid="dept-leaves-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#655dc6]">{t('department_leaves')}</h3>
                {deptLeaves.length > 0 && (
                  <Badge className="bg-red-100 text-red-700 border-red-200">{deptLeaves.length}</Badge>
                )}
              </div>
              {deptLeaves.length === 0 ? (
                <p className="text-[#655dc6]/50 text-sm text-center py-4">{t('no_department_leaves')}</p>
              ) : (
                <>
                {/* Mobile Card List (<640px) */}
                <div className="sm:hidden space-y-3" data-testid="dept-leaves-mobile-list">
                  {deptLeaves.map((leave) => (
                    <div
                      key={leave.id}
                      data-testid={`dept-leave-card-${leave.id}`}
                      className="bg-white dark:bg-slate-900 border border-[#C4BFF0] dark:border-slate-700 rounded-xl p-4 shadow-sm"
                    >
                      {/* Header: employee */}
                      <div className="pb-3 mb-3 border-b border-[#C4BFF0]/40 dark:border-slate-800/80">
                        <h4 className="text-sm font-bold text-[#262263] dark:text-slate-100">{leave.user_name}</h4>
                        {leave.user_name_en && (
                          <p className="text-[11px] text-[#655dc6]/60 dark:text-slate-400 leading-tight" dir="ltr">{leave.user_name_en}</p>
                        )}
                        <p className="text-[11px] text-[#655dc6]/60 dark:text-slate-400 mt-1">
                          {t(`${leave.leave_type}_leave`)}
                        </p>
                      </div>

                      {/* Dates */}
                      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                        <div>
                          <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{t('start_date')}</span>
                          <span className="font-semibold text-slate-800 dark:text-slate-200 font-mono">{leave.start_date}</span>
                        </div>
                        <div>
                          <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{t('end_date')}</span>
                          <span className="font-semibold text-slate-800 dark:text-slate-200 font-mono">{leave.end_date}</span>
                        </div>
                        {leave.reason && (
                          <div className="col-span-2">
                            <span className="block text-[#655dc6]/60 dark:text-slate-500 font-medium mb-0.5">{t('reason')}</span>
                            <span className="text-slate-700 dark:text-slate-300 line-clamp-2">{leave.reason}</span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="pt-3 border-t border-[#C4BFF0]/40 dark:border-slate-800/80">
                        {leave.for_info_only ? (
                          <span
                            className="text-[11px] bg-sky-100 text-sky-800 border border-sky-300 px-2 py-1.5 rounded-full font-medium inline-block"
                            data-testid={`info-only-badge-mobile-${leave.id}`}
                          >
                            {language === 'ar' ? 'للعلم — يعتمدها مدير النظام' : 'Info only — Admin approves'}
                          </span>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleManagerAction(leave.id, 'approve')}
                              disabled={processingLeave === leave.id}
                              className="bg-green-600 hover:bg-green-700 text-white gap-1 min-h-[40px]"
                              data-testid={`mgr-approve-mobile-${leave.id}`}
                            >
                              <CheckCircle size={16} weight="bold" />
                              {t('approve')}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleManagerAction(leave.id, 'reject')}
                              disabled={processingLeave === leave.id}
                              className="border-red-300 text-red-600 hover:bg-red-50 gap-1 min-h-[40px]"
                              data-testid={`mgr-reject-mobile-${leave.id}`}
                            >
                              <XCircle size={16} weight="bold" />
                              {t('reject')}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop Table (>=640px) */}
                <div className="hidden sm:block overflow-x-auto scrollbar-thin" data-mobile-hint="scroll-x">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-start text-[#655dc6]">{t('employee_name')}</TableHead>
                        <TableHead className="text-start text-[#655dc6]">{t('leave_type')}</TableHead>
                        <TableHead className="text-start text-[#655dc6]">{t('start_date')}</TableHead>
                        <TableHead className="text-start text-[#655dc6]">{t('end_date')}</TableHead>
                        <TableHead className="text-start text-[#655dc6]">{t('reason')}</TableHead>
                        <TableHead className="text-start text-[#655dc6]">{t('actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {deptLeaves.map((leave) => (
                        <TableRow key={leave.id}>
                          <TableCell className="font-medium text-[#655dc6]">
                            <div>{leave.user_name}</div>
                            {leave.user_name_en && (
                              <div className="text-[10px] text-[#655dc6]/60 leading-tight" dir="ltr">{leave.user_name_en}</div>
                            )}
                          </TableCell>
                          <TableCell className="text-[#655dc6]/70">{t(`${leave.leave_type}_leave`)}</TableCell>
                          <TableCell className="text-[#655dc6]/70">{leave.start_date}</TableCell>
                          <TableCell className="text-[#655dc6]/70">{leave.end_date}</TableCell>
                          <TableCell className="text-[#655dc6]/70 max-w-[150px] truncate">{leave.reason || '-'}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {leave.for_info_only ? (
                                <span
                                  className="text-[10px] bg-sky-100 text-sky-800 border border-sky-300 px-2 py-1 rounded-full font-medium"
                                  data-testid={`info-only-badge-${leave.id}`}
                                >
                                  {language === 'ar' ? 'للعلم — يعتمدها مدير النظام' : 'Info only — Admin approves'}
                                </span>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => handleManagerAction(leave.id, 'approve')}
                                    disabled={processingLeave === leave.id}
                                    className="bg-green-600 hover:bg-green-700 text-white h-8 gap-1"
                                    data-testid={`mgr-approve-${leave.id}`}
                                  >
                                    <CheckCircle size={16} weight="bold" />
                                    {t('approve')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleManagerAction(leave.id, 'reject')}
                                    disabled={processingLeave === leave.id}
                                    className="border-red-300 text-red-600 hover:bg-red-50 h-8 gap-1"
                                    data-testid={`mgr-reject-${leave.id}`}
                                  >
                                    <XCircle size={16} weight="bold" />
                                    {t('reject')}
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* ============= Leave Balance + Salary Details — side by side ============= */}
        {/* Agency contracted workers (موظف مورد قوى عاملة) get NO benefits view —
            attendance + overtime only. */}
        {!hasNoPaidLeave(user) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Leave Balance */}
          {leaveBalance && (
            <Card className="border border-[#C4BFF0]" data-testid="leave-balance-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#655dc6] mb-4">{t('leave_balance')}</h3>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="p-2 bg-[#E8E6F5] rounded-lg text-center">
                    <p className="text-[10px] text-[#655dc6]/70 leading-tight">
                      {language === 'ar' ? 'الاستحقاق المتوقع (نسبي)' : 'Projected Entitlement'}
                    </p>
                    <p className="text-xl font-bold text-[#655dc6]">{leaveBalance.prorated_entitlement ?? leaveBalance.annual_entitlement}</p>
                    <p className="text-[10px] text-[#655dc6]/50">{t('days')}</p>
                  </div>
                  <div className="p-2 bg-red-50 rounded-lg text-center">
                    <p className="text-[10px] text-red-500 leading-tight">{language === 'ar' ? 'مستخدمة (هذا العام)' : 'Used (year)'}</p>
                    <p className="text-xl font-bold text-red-600">{leaveBalance.used_days}</p>
                    <p className="text-[10px] text-red-400">{t('days')}</p>
                  </div>
                  <div className="p-2 bg-yellow-50 rounded-lg text-center">
                    <p className="text-[10px] text-yellow-600 leading-tight">{t('pending_days')}</p>
                    <p className="text-xl font-bold text-yellow-600">{leaveBalance.pending_days}</p>
                    <p className="text-[10px] text-yellow-500">{t('days')}</p>
                  </div>
                  <div className={`p-2 rounded-lg text-center ${leaveBalance.remaining_days <= 0 ? 'bg-gray-100' : 'bg-green-50'}`}>
                    <p className={`text-[10px] leading-tight ${leaveBalance.remaining_days <= 0 ? 'text-gray-500' : 'text-green-600'}`}>
                      {language === 'ar' ? 'متبقي (طلب إجازة)' : 'Remaining (book)'}
                    </p>
                    <p className={`text-xl font-bold ${leaveBalance.remaining_days <= 0 ? 'text-gray-500' : 'text-green-600'}`}>{leaveBalance.remaining_days}</p>
                    <p className={`text-[10px] ${leaveBalance.remaining_days <= 0 ? 'text-gray-400' : 'text-green-500'}`}>{t('days')}</p>
                  </div>
                </div>

                {/* Explainer shown only for employees with historical salary changes
                    (FIFO logic genuinely differs from prorated). Auto-detected from salary history. */}
                {accrualBreakdown && accrualBreakdown.is_legacy_hire && accrualBreakdown.claimable_days > 0
                  && (leaveBalance.prorated_entitlement || 0) > (accrualBreakdown.claimable_days || 0) && (
                  <div className="mt-2 flex items-start gap-2 p-2 rounded-md bg-blue-50 border border-blue-200" data-testid="balance-explainer">
                    <Info size={14} weight="fill" className="text-blue-600 shrink-0 mt-0.5" />
                    <p className="text-[11px] text-blue-800 leading-snug">
                      {language === 'ar'
                        ? <><strong>الفرق بين الرقمين:</strong> الاستحقاق المتوقع ({leaveBalance.prorated_entitlement} يوم) يشمل الجزء النسبي من الشهر الحالي حتى اليوم. القابل للصرف نقداً ({accrualBreakdown.claimable_days} يوم) يحسب بطريقة FIFO بناءً على رواتبك التاريخية المسجلة.</>
                        : <><strong>Why two numbers?</strong> Projected entitlement ({leaveBalance.prorated_entitlement} d) prorates the current month up to today. Cash-claimable ({accrualBreakdown.claimable_days} d) uses FIFO over your recorded historical salaries.</>}
                    </p>
                  </div>
                )}

                {/* ============ Cash value of accumulated balance (FIFO) ============ */}
                {accrualBreakdown && accrualBreakdown.cash_out_eligible && accrualBreakdown.cash_out_max_days > 0 && (
                  <div className="mt-3 p-3 rounded-lg bg-gradient-to-r from-[#E8E6F5] to-[#F2F0FA] border border-[#C4BFF0] flex items-center justify-between" data-testid="accrual-cash-widget">
                    <div>
                      <p className="text-[11px] text-[#655dc6]/70 leading-tight">
                        {language === 'ar' ? 'القيمة الفعلية للصرف النقدي الآن' : 'Actual cash-out value now'}
                      </p>
                      <p className="text-[10px] text-[#655dc6]/50">
                        {language === 'ar'
                          ? `${accrualBreakdown.cash_out_max_days} يوم (الحد الأقصى) بمعدل ${accrualBreakdown.cash_out_avg_rate?.toLocaleString?.() ?? 0} ر.س / يوم — FIFO تاريخي`
                          : `${accrualBreakdown.cash_out_max_days} d (max) at ${accrualBreakdown.cash_out_avg_rate?.toLocaleString?.() ?? 0} SAR/day — historic FIFO`}
                      </p>
                    </div>
                    <div className="text-end">
                      <p className="text-2xl font-bold text-[#10B981]" data-testid="cash-out-value-display">
                        {accrualBreakdown.cash_out_value_sar?.toLocaleString?.() ?? 0}
                      </p>
                      <p className="text-[10px] text-[#655dc6]/60">{language === 'ar' ? 'ر.س' : 'SAR'}</p>
                    </div>
                  </div>
                )}
                {accrualBreakdown && !accrualBreakdown.cash_out_eligible && (accrualBreakdown.claimable_days || 0) > 0 && (
                  <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800 leading-snug" data-testid="cash-out-ineligible-notice">
                    {language === 'ar'
                      ? `الصرف النقدي يتطلب رصيد لا يقل عن 40 يوماً. رصيدك الحالي: ${accrualBreakdown.claimable_days} يوم.`
                      : `Cash-out requires at least 40 days of balance. Your current balance: ${accrualBreakdown.claimable_days} days.`}
                  </div>
                )}

                {/* ============ Interactive Leave Allowance Calculator ============ */}
                {(() => {
                  // Cash-out policy cap (40 min balance, 30 max/request, 10 residual)
                  // mirrors routes/clearance.py. We surface it via the backend
                  // breakdown so the calculator can never overstate the payout.
                  const cashOutMax = accrualBreakdown?.cash_out_max_days ?? 0;
                  const cashOutEligible = accrualBreakdown?.cash_out_eligible ?? false;
                  const isLegacy = accrualBreakdown?.is_legacy_hire ?? false;
                  // Live value: legacy hires use FIFO preview from API; simple-path uses local multiply.
                  const requested = parseFloat(leavePayoutDays) || 0;
                  const liveValue = isLegacy
                    ? (fifoPreview?.total_value_sar ?? 0)
                    : (requested * (accrualBreakdown?.cash_out_avg_rate || leaveBalance.daily_full_wage || 0));
                  return (
                <div className="mt-4 p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-lg" data-testid="leave-allowance-calculator">
                  <div className="flex items-center gap-2 mb-3">
                    <Calculator size={18} weight="duotone" className="text-emerald-700" />
                    <p className="text-sm font-semibold text-emerald-700">
                      {language === 'ar' ? 'حاسبة بدل الإجازة (صرف نقدي)' : 'Leave Allowance Calculator (Cash Out)'}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
                    <div>
                      <Label className="text-[10px] text-emerald-700/80 block mb-1">
                        {language === 'ar' ? `كم يوم؟ (الحد ${cashOutMax})` : `Days (max ${cashOutMax})`}
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        max={cashOutMax}
                        step="0.5"
                        value={leavePayoutDays}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          if (isNaN(v) || v < 0) { setLeavePayoutDays(''); return; }
                          const capped = Math.min(v, cashOutMax);
                          setLeavePayoutDays(capped);
                        }}
                        placeholder="0"
                        className="text-center bg-white border-emerald-300 focus:ring-emerald-500 h-9"
                        data-testid="leave-payout-input"
                        disabled={!cashOutEligible || cashOutMax <= 0}
                      />
                    </div>

                    <div className="p-2 bg-white/60 rounded text-center">
                      <p className="text-[10px] text-emerald-700/70 leading-tight">
                        {language === 'ar' ? (isLegacy ? 'متوسط FIFO' : 'الراتب اليومي') : (isLegacy ? 'FIFO Avg' : 'Daily Wage')}
                      </p>
                      <p className="text-base font-bold text-emerald-700">
                        {((accrualBreakdown?.cash_out_avg_rate) || leaveBalance.daily_full_wage || 0).toLocaleString()} <Sar />
                      </p>
                    </div>

                    <div className="p-2 bg-emerald-600 rounded text-center text-white">
                      <p className="text-[10px] text-white/80 leading-tight">{language === 'ar' ? 'بدل الإجازة (FIFO)' : 'Allowance (FIFO)'}</p>
                      <p className="text-lg font-extrabold" data-testid="calculator-allowance-value">
                        {fifoLoading
                          ? '...'
                          : liveValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} <Sar />
                      </p>
                    </div>
                  </div>

                  {!cashOutEligible && (
                    <p className="mt-2 text-[11px] text-emerald-700/70 text-center">
                      {language === 'ar'
                        ? 'لا تستوفي شروط الصرف النقدي حالياً (يلزم رصيد 40 يوماً على الأقل).'
                        : 'You are not currently eligible for cash-out (40-day minimum balance required).'}
                    </p>
                  )}
                  {cashOutEligible && cashOutMax <= 0 && (
                    <p className="mt-2 text-[11px] text-emerald-700/70 text-center">
                      {language === 'ar' ? 'لا يوجد رصيد متاح للصرف بعد الاحتفاظ بـ 10 أيام كرصيد متبقي.' : 'No cashable balance available after the 10-day residual rule.'}
                    </p>
                  )}

                  <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800 leading-relaxed flex items-start gap-1.5">
                    <span className="text-amber-600 shrink-0">ℹ️</span>
                    <span>
                      {language === 'ar'
                        ? <><strong>تنبيه:</strong> القيمة محسوبة بطريقة FIFO تاريخية (الأيام الأقدم أولاً بأسعار رواتبها وقت الاستحقاق) — تطابق المبلغ الفعلي الذي ستصرفه الإدارة المالية. قد تنخفض القيمة النهائية بعد الاستقطاعات (تأمينات، غرامات).</>
                        : <><strong>Notice:</strong> Value is computed using historic FIFO (oldest days first at their accrual-time wage) — this matches the amount Finance will actually pay out. The final payout may decrease after deductions (insurance, penalties).</>
                      }
                    </span>
                  </div>
                </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Salary Details Card */}
          {myProfile && myProfile.basic_salary && (
            <Card className="border border-[#C4BFF0]" data-testid="salary-details-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#655dc6] mb-4">{t('salary_details')}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-[#655dc6]/70">{t('basic_salary')}</span><span className="text-[#655dc6] font-medium">{(myProfile.basic_salary || 0).toLocaleString()} <Sar /></span></div>
                  <div className="flex justify-between"><span className="text-[#655dc6]/70">{t('housing_allowance')}</span><span className="text-green-600">+{(myProfile.housing_allowance || 0).toLocaleString()} <Sar /></span></div>
                  <div className="flex justify-between"><span className="text-[#655dc6]/70">{t('transport_allowance')}</span><span className="text-green-600">+{(myProfile.transport_allowance || 0).toLocaleString()} <Sar /></span></div>
                  <div className="flex justify-between"><span className="text-[#655dc6]/70">{t('other_allowances')}</span><span className="text-green-600">+{(myProfile.other_allowances || 0).toLocaleString()} <Sar /></span></div>
                  <Separator />
                  <div className="flex justify-between pt-1"><span className="font-semibold text-[#655dc6]">{t('net_salary')}</span><span className="font-bold text-[#655dc6] text-lg">{(myProfile.salary || 0).toLocaleString()} <Sar /></span></div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        )}

        {/* ============= EOS Cards — side by side ============= */}
        {!hasNoPaidLeave(user) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* EOS — Resignation */}
          {eosResignation && (
            <Card className="border border-[#C4BFF0] relative overflow-hidden" data-testid="eos-resignation-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-[#655dc6] flex items-center gap-2">
                    <span>{language === 'ar' ? 'مكافأة نهاية الخدمة (استقالة)' : 'EOS — Resignation'}</span>
                    <span className="text-2xl">😢</span>
                  </h3>
                </div>
                <div className="p-4 bg-gradient-to-br from-[#655dc6] to-[#544cb0] rounded-xl text-white text-center mb-3">
                  <p className="text-white/80 text-sm">{t('eos_amount')}</p>
                  <p className="text-3xl font-bold">{(eosResignation.eos_amount || 0).toLocaleString()} <Sar /></p>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                  <div className="p-2 bg-[#E8E6F5] rounded text-center">
                    <p className="text-xs text-[#655dc6]/70">{t('years_of_service')}</p>
                    <p className="font-bold text-[#655dc6]">{eosResignation.years_of_service}</p>
                  </div>
                  <div className="p-2 bg-[#E8E6F5] rounded text-center">
                    <p className="text-xs text-[#655dc6]/70">{t('daily_wage')}</p>
                    <p className="font-bold text-[#655dc6]">{eosResignation.daily_wage} <Sar /></p>
                  </div>
                </div>
                <div className="p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800 leading-relaxed">
                  {language === 'ar'
                    ? '⚠️ المبلغ محسوب على أساس إنهاء الخدمة بطلب الاستقالة وفق المادة 85 من نظام العمل (أقل من سنتين: لا يستحق، 2–5 سنوات: ⅓ المكافأة، 5–10 سنوات: ⅔ المكافأة، 10+ سنوات: المكافأة كاملة).'
                    : '⚠️ Calculated for voluntary resignation per Labor Law Art. 85 (<2y: not entitled, 2–5y: ⅓, 5–10y: ⅔, 10y+: full).'}
                </div>
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800 leading-relaxed flex items-start gap-1.5">
                  <span className="text-amber-600 shrink-0">ℹ️</span>
                  <span>
                    {language === 'ar'
                      ? <><strong>تنبيه:</strong> هذا تقدير أولي ويُعدّ <strong>الحد الأقصى</strong>. المبلغ النهائي قد <strong>ينخفض</strong> بعد مراجعة الإدارة المالية لاستقطاع أي خصومات (تأمينات، غرامات...). لن يزيد عن المبلغ المعروض.</>
                      : <><strong>Notice:</strong> This is a preliminary <strong>maximum</strong> estimate. The final amount may <strong>decrease</strong> after Finance review (insurance, deductions). It will not exceed the amount shown.</>
                    }
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* EOS — Contract End / Termination */}
          {eosTermination && (
            <Card className="border border-[#C4BFF0]" data-testid="eos-termination-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-[#655dc6] flex items-center gap-2">
                    <span>{language === 'ar' ? 'مكافأة نهاية الخدمة (نهاية العقد)' : 'EOS — Contract End'}</span>
                    <span className="text-2xl">🤝</span>
                  </h3>
                </div>
                <div className="p-4 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl text-white text-center mb-3">
                  <p className="text-white/80 text-sm">{t('eos_amount')}</p>
                  <p className="text-3xl font-bold">{(eosTermination.eos_amount || 0).toLocaleString()} <Sar /></p>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                  <div className="p-2 bg-emerald-50 rounded text-center">
                    <p className="text-xs text-emerald-700/70">{t('years_of_service')}</p>
                    <p className="font-bold text-emerald-700">{eosTermination.years_of_service}</p>
                  </div>
                  <div className="p-2 bg-emerald-50 rounded text-center">
                    <p className="text-xs text-emerald-700/70">{t('daily_wage')}</p>
                    <p className="font-bold text-emerald-700">{eosTermination.daily_wage} <Sar /></p>
                  </div>
                </div>
                <div className="p-2 bg-emerald-50 border border-emerald-200 rounded text-[11px] text-emerald-800 leading-relaxed">
                  {language === 'ar'
                    ? '✓ المبلغ محسوب على أساس انتهاء العقد أو إنهاء صاحب العمل: نصف شهر لكل سنة من السنوات الخمس الأولى + شهر كامل لكل سنة بعدها.'
                    : '✓ Calculated for contract end or employer termination: half-month per year for the first 5 years + full month per year after that.'}
                </div>
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800 leading-relaxed flex items-start gap-1.5">
                  <span className="text-amber-600 shrink-0">ℹ️</span>
                  <span>
                    {language === 'ar'
                      ? <><strong>تنبيه:</strong> هذا تقدير أولي ويُعدّ <strong>الحد الأقصى</strong>. المبلغ النهائي قد <strong>ينخفض</strong> بعد مراجعة الإدارة المالية لاستقطاع أي خصومات (تأمينات، غرامات...). لن يزيد عن المبلغ المعروض.</>
                      : <><strong>Notice:</strong> This is a preliminary <strong>maximum</strong> estimate. The final amount may <strong>decrease</strong> after Finance review (insurance, deductions). It will not exceed the amount shown.</>
                    }
                  </span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        )}

      </main>

      {/* Pending-custody signing dialog */}
      <Dialog open={!!signCustodyTarget} onOpenChange={(o) => !o && setSignCustodyTarget(null)}>
        <DialogContent dir={direction} className="max-w-md" data-testid="sign-custody-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-800">
              {language === 'ar' ? 'توقيع استلام العهدة' : 'Sign custody receipt'}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {language === 'ar'
                ? 'تأكد من تطابق بيانات الصنف قبل التوقيع. التوقيع الرقمي يعادل التوقيع اليدوي ويظهر على سند استلام PDF.'
                : "Verify the asset details before signing. Your digital signature is legally equivalent and appears on the PDF receipt."}
            </DialogDescription>
          </DialogHeader>
          {signCustodyTarget && (
            <div className="space-y-3 pt-2">
              <div className="rounded-md border border-[#C4BFF0] bg-[#F8F7FC] p-3 text-sm">
                <div><strong>{language === 'ar' ? 'الصنف' : 'Asset'}:</strong> {signCustodyTarget.asset?.name}</div>
                {signCustodyTarget.asset?.serial_number && (
                  <div className="font-mono text-xs text-gray-600 mt-1">SN: {signCustodyTarget.asset.serial_number}</div>
                )}
                {signCustodyTarget.handover_notes && (
                  <div className="text-xs text-gray-700 mt-2 border-t pt-2">
                    <strong>{language === 'ar' ? 'ملاحظات' : 'Notes'}:</strong> {signCustodyTarget.handover_notes}
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs text-[#4B5563] font-semibold">{language === 'ar' ? 'الاسم الكامل' : 'Full name'}</label>
                <Input value={signCustodyName} onChange={(e) => setSignCustodyName(e.target.value)} className="mt-1" data-testid="sign-custody-name" />
              </div>
              <div>
                <label className="text-xs text-[#4B5563] font-semibold">{language === 'ar' ? 'توقيعك الرقمي' : 'Your digital signature'} *</label>
                <div className="mt-1 border border-[#C4BFF0] rounded-md bg-white">
                  <SignaturePad ref={signCustodyPadRef} height={140} onChange={(empty) => setSignCustodyEmpty(empty)} data-testid="sign-custody-pad" />
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[11px] text-gray-500">{language === 'ar' ? 'يُحفظ التوقيع على سند الاستلام' : 'Appears on the PDF receipt'}</span>
                  <button type="button" onClick={() => { signCustodyPadRef.current?.clear(); setSignCustodyEmpty(true); }} className="text-xs text-[#655dc6] hover:underline">
                    {language === 'ar' ? 'مسح' : 'Clear'}
                  </button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignCustodyTarget(null)} disabled={signingCustody}>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={submitCustodySignature} disabled={signingCustody} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="sign-custody-submit">
              {signingCustody ? '...' : (language === 'ar' ? 'توقيع وتأكيد الاستلام' : 'Sign & confirm receipt')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
